import { Star } from 'lucide-react';
import { HOME } from '@/constants/testIds';

// Real customer reviews collected via DM / Instagram / email.
// Each review can carry its own accent so the wall doesn't feel flat.
const REVIEWS = [
  {
    name: 'Chamberlain',
    accent: '#00B4D8', // teal
    body:
      "I have had several custom shirts made — they're great quality and very durable. I love being able to have something so custom.",
  },
  {
    name: 'Kyle',
    accent: '#8B5CF6', // purple
    body:
      'I have ordered a couple of shirts for my wife and myself. The shirts were of great quality and the graphics turned out amazing. I would highly recommend purchasing and definitely will for myself in the future.',
  },
  {
    name: 'Juanita',
    accent: '#EC4899', // pink
    body:
      'Shirt Happens Studio brought my vision to life perfectly! The quality of the shirts is amazing, the designs are beautiful, and the customer service was outstanding. You can truly see the care and passion that goes into every order. I\u2019ll definitely be ordering again.',
    highlight: 'Highly Recommend Shirt Happens Studio!',
  },
  {
    name: 'Adrian',
    accent: '#A855F7', // magenta / royal purple
    body:
      "I came to Roger for shirts and sweaters for our Men's Church Group and he set us up with some great gear! Shirts are Great Quality \u0026 he works with you to bring your shirt to life! Turn around time was quick and professional! We will be using his services for an August Men's Event as well!",
    highlight: 'Five stars for sure!',
  },
];

const StarRow = () => (
  <div className="flex items-center gap-0.5" aria-label="5 out of 5 stars">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={16} className="fill-brand-orange text-brand-orange" strokeWidth={0} />
    ))}
  </div>
);

const ReviewCard = ({ review, index }) => (
  <article
    data-testid={`${HOME.reviewCard}-${index}`}
    className="relative group rounded-3xl bg-white border-2 border-black p-6 md:p-7 flex flex-col shadow-[6px_6px_0_#000] hover:shadow-[10px_10px_0_#000] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all duration-200"
  >
    {/* Accent corner block — nods to the source cards */}
    <div
      aria-hidden
      className="absolute -top-1 -left-1 w-10 h-10 rounded-tl-3xl"
      style={{ background: review.accent }}
    />
    <div
      aria-hidden
      className="absolute -bottom-1 -right-1 w-10 h-10 rounded-br-3xl"
      style={{ background: review.accent }}
    />

    <div className="flex items-center justify-between mb-4 relative z-10">
      <StarRow />
      <div className="font-display font-black text-[10px] tracking-[0.32em] uppercase text-black/50">
        Verified customer
      </div>
    </div>

    {review.highlight && (
      <div
        className="font-display font-black text-base sm:text-lg text-black leading-snug mb-3 relative z-10"
      >
        {review.highlight}
      </div>
    )}

    <p className="font-body text-sm sm:text-[15px] text-black/80 leading-relaxed relative z-10 flex-1">
      &ldquo;{review.body}&rdquo;
    </p>

    <div className="mt-5 pt-4 border-t-2 border-dashed border-black/10 relative z-10 flex items-center gap-3">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center font-display font-black text-white text-sm"
        style={{ background: review.accent }}
      >
        {review.name.charAt(0)}
      </div>
      <div>
        <div className="font-display font-black text-black text-sm tracking-wide">
          {review.name}
        </div>
        <div className="font-body text-[11px] uppercase tracking-[0.24em] text-black/50">
          Shirt Happens customer
        </div>
      </div>
    </div>
  </article>
);

const ReviewsSection = () => {
  return (
    <section
      id="reviews"
      data-testid={HOME.reviewsSection}
      className="relative py-24 md:py-28 px-6 bg-brand-cream overflow-hidden"
    >
      {/* Soft brand halo */}
      <div
        aria-hidden
        className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-20 blur-3xl"
        style={{ background: 'radial-gradient(circle, #FF6600 0%, transparent 70%)' }}
      />
      <div className="max-w-6xl mx-auto relative">
        <div className="mb-14 md:mb-16 max-w-3xl">
          <div className="inline-flex items-center gap-2 mb-4">
            <StarRow />
            <span className="font-display font-black text-[11px] tracking-[0.32em] uppercase text-brand-orange">
              5-star reviews
            </span>
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-black mb-4">
            Real people. <br className="hidden sm:block" />
            <span className="text-brand-orange">Real shirts.</span> Real happy.
          </h2>
          <p className="font-body text-base sm:text-lg text-black/70 leading-relaxed">
            The best part of the job is when the shirts land and people actually love them.
            Here&rsquo;s what a few customers had to say.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-7">
          {REVIEWS.map((review, i) => (
            <ReviewCard key={review.name} review={review} index={i} />
          ))}
        </div>

        <div className="mt-14 md:mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t-2 border-black/10">
          <div>
            <div className="font-display font-black text-2xl text-black">Want your name here next?</div>
            <div className="font-body text-sm text-black/60 mt-1">
              Start a custom idea below — I&rsquo;ll take care of the rest.
            </div>
          </div>
          <a
            href="#custom"
            className="inline-flex items-center gap-2 font-display font-black text-xs uppercase tracking-[0.24em] px-6 py-3.5 rounded-full bg-black text-brand-orange hover:bg-brand-orange hover:text-black border-2 border-black transition-colors"
          >
            Start my shirt →
          </a>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
